import { ContainerPadding, CTA, Text, TextAlignment, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useCallback, useEffect } from 'react';
import { CloseLayerCTA } from '../../components/cta/close-layer-cta';
import { ConsentimientosEnvioSection } from '../../components/inputs-group/consentimientos-envio-section';
import { DatosPersonalesInputsGroup } from '../../components/inputs-group/datos-personales-inputs-group';
import { ComentarioInput } from '../../components/inputs-group/inputs/comentario-input';
import { CtasRadio } from '../../components/inputs-group/inputs/core/ctas-radio';
import { DealersRadio } from '../../components/inputs-group/inputs/dealers-radio';
import { VehicleSection } from '../../components/inputs-group/inputs/vehicle-section/vehicle-section';
import { LegalesSection } from '../../components/inputs-group/legales-section';
import { Section } from '../../components/screen/section';
import { ThankyouScreen } from '../../components/screen/thankyou';
import { FIELD_NAME } from '../../constants/form-constants';
import { useApp } from '../../contexts/app-context';
import { dealerEntity } from '../../entities/dealer-entity';
import { ndpsEntity } from '../../entities/ndps-entity';
import { personEntity } from '../../entities/person-entity';
import { vehicleEntity } from '../../entities/vehicle-entity';
import { formService } from '../../form-service';
import { isComerciales } from '../../helpers/carline-helpers';
import {
	isLeadVentaCitaForm,
	isLeadVentaMasInformacionForm,
	isLeadVentaPresupuestoForm,
	isLeadVentaStockForm,
	isLeadVentaTestDriveForm,
	isLeadVentaVehiculoOcasionForm,
} from '../../helpers/form-helpers';
import { getLeadVentaProductData } from './lead-venta-product-data';

export const LeadVentaForm = () => {
	const { setAppThankyouScreen } = useApp();

	const getFormData = useCallback(() => {
		const ndpsToken = isComerciales() ? 'LCV_DDB_FRM_LEM_REC_LEM' : 'VW_DDB_FRM_LEM_REC_LEM';
		ndpsEntity.setToken(ndpsToken);

		return {
			'headers[FK_SYSTEM]': ndpsToken,
			'headers[DPS_TOKEN]': ndpsToken,

			'flags[PERSON_RGPD_ADAPTED]': true,
			'flags[FLAG_IS_MYSTERY_LEAD]': false,
			'flags[FLAG_RELACION_MARCA_SIN_IC]': false,
			'flags[FLAG_SF_INTEGRATION]': true,
			'flags[FLAG_IS_COMPANY]': isComerciales() ? false : undefined,
			'flags[FLAG_ALLOWS_DATA_ENRICHMENT]': isComerciales() ? undefined : false,

			'fields[PERSON_TREATMENT]': isComerciales() ? personEntity.treatment : undefined,
			'fields[PERSON_NAME]': personEntity.name,
			'fields[PERSON_SURNAME_1]': personEntity.surname,
			'fields[PERSON_SURNAME_2]': personEntity.secondSurname,
			'fields[PERSON_EMAIL]': personEntity.email,
			'fields[PERSON_PHONE_PREFIX]': personEntity.phonePrefix,
			'fields[PERSON_PHONE_MOBILE]': personEntity.phone,
			'fields[PERSON_PHONE_MOBILE_FULL]': personEntity.phonePrefix + personEntity.phone,
			'fields[PERSON_POSTAL_CP]': personEntity.postalCode,
			'fields[PERSON_PREFERRED_SLOT]': formService.getFieldValue('franjaHoraria'),
			'fields[COMMENTS_PERSON]': formService.getFieldValue(FIELD_NAME.COMMENT),

			// PRODUCT DATA (cada data sobreescribe valores del anterior)
			...ndpsEntity.dealerData,
			...ndpsEntity.vehicleData,
		};
	}, []);

	const optionsKilometros = [
		{
			text: 'Hasta 25.000 Km',
			value: '25.000',
		},
		{
			text: 'Hasta 50.000 Km',
			value: '50.000',
		},
		{
			text: 'Hasta 100.000 Km',
			value: '100.000',
		},
		{
			text: 'Hasta 180.000 Km',
			value: '180.000',
		},
	];
	const optionsAntiguedad = [
		{
			text: '0 a 2 años',
			value: '0-2',
		},
		{
			text: '2 a 5 años',
			value: '2-5',
		},
		{
			text: '5 a 8 años',
			value: '5-8',
		},
	];
	const optionsFranjaHoraria = [
		{
			text: 'Por la mañana',
			value: 'MANANA',
		},
		{
			text: 'Al mediodía',
			value: 'MEDIODIA',
		},
		{
			text: 'Por la tarde',
			value: 'TARDE',
		},
	];

	const Form = () => (
		<>
			<VehicleSection paddingBottom={ContainerPadding.static515} />
			{isLeadVentaVehiculoOcasionForm() && (
				<>
					<Section paddingBottom={ContainerPadding.static515}>
						<Text bold appearance={TokenTextAppearance.headline200}>
							¿Cuántos kilómetros quieres que tenga como máximo el coche?
						</Text>
						<CtasRadio name="kilometros" options={optionsKilometros} required />
					</Section>
					<Section paddingBottom={ContainerPadding.static515}>
						<Text bold appearance={TokenTextAppearance.headline200}>
							¿Qué antigüedad te gustaría que tuviera el coche?
						</Text>
						<CtasRadio name="antiguedad" options={optionsAntiguedad} required />
					</Section>
				</>
			)}
			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Cómo nos ponemos en contacto contigo?
				</Text>
				<Text appearance={TokenTextAppearance.copy200}>
					El responsable de los datos de contacto y preferencias que nos proporciones será Volkswagen Group España
					Distribución, S.A.U., que los compartirá con el concesionario que hayas seleccionado, con el fin de que este
					pueda dar respuesta a tu solicitud. El concesionario tratará tus datos personales como encargado del
					tratamiento.
				</Text>
				<DatosPersonalesInputsGroup />
			</Section>
			{isLeadVentaMasInformacionForm() && (
				<Section paddingBottom={ContainerPadding.static515}>
					<Text bold appearance={TokenTextAppearance.headline200}>
						¿Quieres saber algo en concreto?
					</Text>
					<ComentarioInput />
				</Section>
			)}
			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Qué instalación te queda más cerca?
				</Text>
				<Text appearance={TokenTextAppearance.copy200}>
					Haremos llegar tus datos al punto de venta para que puedan ayudarte.
				</Text>
				<DealersRadio />
			</Section>
			{isLeadVentaCitaForm() && (
				<Section paddingBottom={ContainerPadding.static515}>
					<Text bold appearance={TokenTextAppearance.headline200}>
						¿Cúando prefieres pasar por el punto de venta?
					</Text>
					<CtasRadio name="franjaHoraria" options={optionsFranjaHoraria} required />
				</Section>
			)}
			<ConsentimientosEnvioSection formData={getFormData} showComunicacionesComerciales />
			<LegalesSection />
		</>
	);

	const CustomThankyou = () => {
		const ctasThankyouScreen = [
			vehicleEntity.showroomUrl ? (
				<CTA tag="a" href={vehicleEntity.showroomUrl} emphasis="primary">
					Conócelo
				</CTA>
			) : null,
			<CloseLayerCTA label="Seguir navegando" />,
		];
		return (
			<ThankyouScreen
				title={
					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center} bold>
						{isLeadVentaCitaForm() && `Te esperamos en ${dealerEntity.name || ''}`}
						{isLeadVentaMasInformacionForm() &&
							`Pronto recibirás más información sobre ${vehicleEntity.carlineTitle || 'Volkswagen'}.`}
						{isLeadVentaTestDriveForm() && `Te esperamos en ${dealerEntity.name || ''}`}
						{isLeadVentaPresupuestoForm() &&
							`Pronto recibirás el presupuesto para tu ${vehicleEntity.carlineTitle || 'Volkswagen'}.`}
						{isLeadVentaStockForm() && 'Pronto responderemos a tus preguntas'}
						{isLeadVentaVehiculoOcasionForm() &&
							`Muy pronto recibirás noticias sobre tu próximo ${vehicleEntity.carlineTitle || 'Volkswagen'} de ocasión`}
					</Text>
				}
				text={
					<Text appearance={TokenTextAppearance.copy100} textAlign={TextAlignment.center}>
						{isLeadVentaCitaForm() &&
							`Ya hemos recibido tu solicitud, ${personEntity.name || ''}. Muy pronto, un asesor contactará contigo para terminar de cerrar tu cita.`}
						{isLeadVentaMasInformacionForm() && 'Un asesor contactará contigo para responder a todas tus preguntas.'}
						{isLeadVentaTestDriveForm() &&
							'Un asesor te llamará pronto para terminar de confirmar contigo los detalles de la prueba de conducción.'}
						{isLeadVentaPresupuestoForm() &&
							'Un asesor contactará contigo para hacerte llegar la información que necesitas.'}
						{isLeadVentaStockForm() &&
							'Un asesor contactará contigo para explicártelo todo sobre nuestros modelos con disponibilidad inmediata.'}
						{isLeadVentaVehiculoOcasionForm() &&
							'Un asesor contactará contigo para darte toda la información que necesites.'}
					</Text>
				}
				ctas={ctasThankyouScreen}
			/>
		);
	};

	useEffect(() => {
		//setAppLoading(true);
		setAppThankyouScreen(<CustomThankyou />);

		getLeadVentaProductData().then(() => {
			//setAppLoading(false);
		});
	}, []);

	return Form();
};
